/**
 * AWARE Email Signature Banner (Jun 2026).
 *
 * Shared by the Partner "Marketing & Referrals" page and the Admin
 * Partner Marketing Kit verification view.
 *
 * The artwork is a fixed, pre-approved PNG shipped in
 * /public/marketing — it is never generated or altered here.
 * The banner always links to the partner's own AWARE landing page
 * with src=email_signature&cmp=pws_partner_signature appended, so
 * attribution stays with the partner.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Download, Code2, Mail, ExternalLink } from "lucide-react";

const GMAIL_STEPS = [
  "In Gmail, open Settings (gear icon) → See all settings → General.",
  "Scroll to Signature, then pick an existing signature or click Create new.",
  "Click into the signature box, then use the Insert image button (mountain icon) → Web address (URL) and paste the banner image URL below.",
  "With the inserted image selected, choose Medium (or Original) size, then click the Link button (chain icon) and paste your tracked link below.",
  "Scroll to the bottom and click Save changes.",
];

const OUTLOOK_WEB_STEPS = [
  "In Outlook on the web, open Settings (gear icon) → Mail → Compose and reply.",
  "Under Email signature, place your cursor where the banner should sit.",
  "Use Insert pictures inline to upload the banner you downloaded below.",
  "Select the image, click the Link button and paste your tracked link below.",
  "Click Save.",
];

const OUTLOOK_DESKTOP_STEPS = [
  "In Outlook for Windows or Mac, go to File → Options → Mail → Signatures (Mac: Outlook → Settings → Signatures).",
  "Create or edit a signature, then click Picture and select the banner PNG you downloaded below.",
  "Select the inserted image, click Hyperlink (or Link) and paste your tracked link below.",
  "Click OK / Save, and set the signature for New messages and Replies/forwards.",
];

function StepList({ title, steps, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <ol className="mt-2 space-y-1.5 text-sm text-slate-600 list-decimal pl-5">
        {steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
    </div>
  );
}

export const EmailSignatureBannerCard = ({ sig, testidPrefix = "email-signature" }) => {
  const [copied, setCopied] = useState(null);

  if (!sig) return null;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    } catch {
      toast.error("Couldn't copy — please select the text manually.");
    }
  };

  const previewSrc = sig.banner_download_path || sig.banner_image_url;
  const target = sig.target_url || "";

  if (!sig.eligible || !target) {
    return (
      <section
        data-testid={`${testidPrefix}-unavailable`}
        className="rounded-xl border border-slate-200 bg-white p-5"
      >
        <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
          <Mail className="w-4 h-4 text-cyan-600" /> AWARE email signature banner
        </div>
        <p className="mt-2 text-sm text-slate-600">
          Your signature banner becomes available once your partner
          referral slug is issued. Nothing else is needed from you.
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid={`${testidPrefix}-card`}
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
        <Mail className="w-4 h-4 text-cyan-600" /> AWARE email signature banner
      </div>
      <p className="mt-1 text-sm text-slate-600 max-w-2xl">
        Add this approved banner to the bottom of your everyday emails.
        Every click lands on your own AWARE page, so the water test is
        credited to you.
      </p>

      {/* Clickable, partner-specific preview */}
      <a
        href={target}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testidPrefix}-preview-link`}
        className="mt-4 block rounded-lg overflow-hidden border border-slate-200 hover:border-cyan-300 transition-colors"
      >
        <img
          src={previewSrc}
          alt={sig.alt_text}
          width={sig.display_width}
          height={sig.display_height}
          data-testid={`${testidPrefix}-preview-image`}
          className="block w-full h-auto"
        />
      </a>
      <div className="mt-1.5 flex items-center gap-1 text-xs text-slate-500">
        <ExternalLink className="w-3 h-3" /> Preview shown at full width —
        it sits at {sig.display_width}×{sig.display_height}px inside an email.
      </div>

      {/* Tracked link */}
      <div className="mt-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Your tracked link
        </div>
        <div
          data-testid={`${testidPrefix}-target-url`}
          className="mt-1 font-mono text-xs text-slate-800 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 break-all"
        >
          {target}
        </div>
        <div className="mt-1 text-xs text-slate-500">
          Source <code className="text-slate-700">{sig.source}</code> · campaign{" "}
          <code className="text-slate-700">{sig.campaign}</code>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        <a
          href={previewSrc}
          download={sig.banner_filename}
          data-testid={`${testidPrefix}-download-btn`}
          className="inline-flex items-center gap-1.5 rounded-full bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 transition-colors"
        >
          <Download className="w-4 h-4" /> Download banner
        </a>
        <button
          type="button"
          onClick={() => copy(target, "link")}
          data-testid={`${testidPrefix}-copy-link-btn`}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
        >
          {copied === "link" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied === "link" ? "Copied" : "Copy link"}
        </button>
        <button
          type="button"
          onClick={() => copy(sig.html, "html")}
          data-testid={`${testidPrefix}-copy-html-btn`}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
        >
          {copied === "html" ? <Check className="w-4 h-4" /> : <Code2 className="w-4 h-4" />}
          {copied === "html" ? "Copied" : "Copy HTML"}
        </button>
        <button
          type="button"
          onClick={() => copy(sig.banner_image_url, "image")}
          data-testid={`${testidPrefix}-copy-image-url-btn`}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
        >
          {copied === "image" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied === "image" ? "Copied" : "Copy image URL"}
        </button>
      </div>

      <details className="mt-4 group" data-testid={`${testidPrefix}-html-details`}>
        <summary className="cursor-pointer text-sm font-semibold text-cyan-700">
          View the signature HTML
        </summary>
        <pre
          data-testid={`${testidPrefix}-html-code`}
          className="mt-2 whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3"
        >
          {sig.html}
        </pre>
      </details>

      <div className="mt-5 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          How to install it
        </div>
        <StepList
          title="Gmail"
          steps={GMAIL_STEPS}
          testid={`${testidPrefix}-steps-gmail`}
        />
        <StepList
          title="Outlook on the web (Microsoft 365)"
          steps={OUTLOOK_WEB_STEPS}
          testid={`${testidPrefix}-steps-outlook-web`}
        />
        <StepList
          title="Outlook desktop (Windows & Mac)"
          steps={OUTLOOK_DESKTOP_STEPS}
          testid={`${testidPrefix}-steps-outlook-desktop`}
        />
        <p className="text-xs text-slate-500">
          If your email tool lets you paste HTML directly, use{" "}
          <b>Copy HTML</b> instead — it already contains the image, the
          link, the alt text and email-safe sizing. Outlook desktop
          needs the downloaded PNG rather than a WebP, which is why the
          banner is supplied as a PNG.
        </p>
        {sig.note && (
          <p
            data-testid={`${testidPrefix}-note`}
            className="text-xs text-slate-500"
          >
            {sig.note}
          </p>
        )}
      </div>
    </section>
  );
};

export default EmailSignatureBannerCard;
